import React, { useState, useEffect } from 'react';
import { Home, Users, Bath, Car, Bed } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import TitleBanner from './TitleBanner';

const BASE_URL = process.env.REACT_APP_BASE_URL;
const HOUSE_TYPE_OPTIONS = [
  'SDA High Physical',
  'SDA Robust',
  'SDA Improved Liveability',
  'SDA Fully Accessible',
  'Non-SDA',
];
const HomeFinder = () => {
  const navigate = useNavigate();
  const [homes, setHomes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    state: '',
    houseType: '',
    region: '',
    propertyType: 'Vacancy' // Default to showing Vacancies
  });
  
  // State to store unique filter options
  const [filterOptions, setFilterOptions] = useState({
    states: new Set(),
    houseTypes: new Set(),
    regions: new Set()
  });

  useEffect(() => {
    fetchHomes();
  }, []);

  // Extract unique filter options from homes data
  useEffect(() => {
    const options = homes.reduce((acc, home) => {
      // Add state if not empty
      if (home.state?.trim()) acc.states.add(home.state.trim());
      
      // Add area as region if not empty
      if (home.area?.trim()) acc.regions.add(home.area.trim());
      
      return acc;
    }, {
      states: new Set(),
      regions: new Set()
    });

    setFilterOptions({
      states: options.states,
      regions: options.regions,
      houseTypes: new Set(HOUSE_TYPE_OPTIONS), 
    });
  }, [homes]);

  const fetchHomes = async () => {
    try {
      const response = await fetch(BASE_URL+'/api/homes');
      const data = await response.json();
      setHomes(data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching homes:', error);
      setLoading(false);
    }
  };

  const getImageUrl = (homeId, filename) => {
    return `${BASE_URL}/api/homes/${homeId}/images/${filename}`;
  };

  const handleReset = () => {
    setFilters({
      state: '',
      houseType: '',
      region: '',
      propertyType: filters.propertyType // Maintain current propertyType when resetting
    });
  };

  const handleHomeClick = (homeId) => {
    navigate(`/ViewHome/${homeId}`);
  };

  // Filter homes based on selected criteria
  const filteredHomes = homes.filter(home => {
    const stateMatch = !filters.state || home.state === filters.state;
    const building = home.building?.toLowerCase().trim() || '';
    const houseTypeMatch =
      !filters.houseType ||

      // Normal direct match
      home.building === filters.houseType ||

      // Improved Liveability special case
      (
        filters.houseType === 'Improved Liveability' &&
        home.houseCategory === 'Improved Liveable'
      ) ||

      (
        filters.houseType === 'SDA Improved Liveability' &&
        home.houseCategory === 'Improved Liveable' &&
        building.includes('sda')
      ) ||

      // Non-SDA: building does NOT contain 'sda' OR is blank
      (
        filters.houseType === 'Non-SDA' &&
        (!building || !building.includes('sda'))
    );
    const regionMatch = !filters.region || home.area === filters.region;
    const propertyTypeMatch = home.propertyUploadType === filters.propertyType;
    
    return stateMatch && houseTypeMatch && regionMatch && propertyTypeMatch;
  });

  return (
    <>
    <TitleBanner image="/Website_Title_Images/Disability_Housing_Vacancies.svg" title={"Disability Housing Vacancies"} subheading={"Check out our vacancies!"} />
    <div className="w-full max-w-7xl mx-auto p-4 pt-12">
      <div className="space-y-4 md:space-y-6 mb-12">
          <h2 className="text-5xl font-bold text-primary mb-6 font-arial-rounded">What is Supported Independent Living?</h2>
          <p className="text-gray-800 text-base md:text-lg">
              Supported Independent Living (SIL) at <strong>Livability Care Australia</strong> offers individuals with disabilities the opportunity to live as independently as possible, with tailored support to assist with daily activities. We aim to empower our participants to live in their own homes or in shared accommodation, while providing the assistance they need for tasks such as personal care, household chores, and other daily activities.
          </p>
          
          <p className="text-gray-800 text-base md:text-lg mb-12">
              At <strong>Livability Care Australia</strong>, we’re dedicated to empowering you to live independently while ensuring you receive the care and support you deserve. If you’re considering SIL as part of your NDIS plan, our team is here to guide you through your options and provide the support you need to achieve your goals.
          </p>
      </div>



      {/* Header */}
      <div className="text-center mb-8 p-8 border-primary border-2 rounded-xl shadow-lg">
        <h1 className="text-6xl font-bold text-primary mb-6 font-arial-rounded">FIND YOUR HOME</h1>
        
        {/* Updated Filter Buttons */}
        <div className="flex flex-wrap gap-4 justify-center mb-4">
          <button 
            onClick={() => setFilters(prev => ({ ...prev, propertyType: 'Vacancy' }))}
            className={`px-4 py-2 rounded-md ${
              filters.propertyType === 'Vacancy'
                ? 'bg-primary text-white'
                : 'bg-white border-2 border-primary text-primary'
            }`}
          >
            Vacancies
          </button>
          <button 
            onClick={() => setFilters(prev => ({ ...prev, propertyType: 'Open House' }))}
            className={`px-4 py-2 rounded-md ${
              filters.propertyType === 'Open House'
                ? 'bg-primary text-white'
                : 'bg-white border-2 border-primary text-primary'
            }`}
          >
            Open House
          </button>
        </div>

        {/* Dynamic Filters */}
        <div className="flex flex-wrap gap-4 justify-center items-center">
          <select 
            value={filters.state}
            onChange={(e) => setFilters({...filters, state: e.target.value})}
            className="w-[180px] px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="">State</option>
            {[...filterOptions.states].sort().map(state => (
              <option key={state} value={state}>{state}</option>
            ))}
          </select>

          <select
            value={filters.houseType}
            onChange={(e) => setFilters({...filters, houseType: e.target.value})}
            className="w-[180px] px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="">House Type</option>
            {[...filterOptions.houseTypes].map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>

          <select
            value={filters.region}
            onChange={(e) => setFilters({...filters, region: e.target.value})}
            className="w-[180px] px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="">Region</option>
            {[...filterOptions.regions].sort().map(region => (
              <option key={region} value={region}>{region}</option>
            ))}
          </select>

          <button 
            onClick={handleReset}
            className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary"
          >
            RESET
          </button>
        </div>
      </div>

      {/* Home Grid */}
      {loading ? (
        <div className="text-center">Loading...</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 py-24">
          {filteredHomes.map((home) => (
            <div 
              key={home.id} 
              className="bg-white rounded-xl border-primary border-2 overflow-hidden shadow-lg cursor-pointer hover:shadow-2xl transition-shadow"
              onClick={() => handleHomeClick(home.id)}
            >
              {/* Property Image */}
              <div className="relative">
                <div className="absolute top-4 left-4 bg-white px-3 py-1 rounded-md">
                  <span className="font-semibold">{home.area}, {home.state}</span>
                </div>
                {home.images?.length > 0 && (
                  <img
                    src={getImageUrl(home.id, home.images[0])}
                    alt={home.propertyName}
                    className="w-full h-48 object-cover"
                  />
                )}
              </div>

              <div className="p-4">
                {/* Property Name and Building */}
                <div className="mb-4">
                  <h3 className="text-xl font-bold text-primary">{home.propertyName}</h3>
                  {home.building && <p className="text-gray-600">{home.building}</p>}
                </div>

                {/* Property Features */}
                <div className="flex justify-between items-center mb-4">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-1">
                      <Bed size={16} />
                      <span>{home.bedrooms} + {home.guestRooms}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Bath size={16} />
                      <span>{home.bathrooms}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Car size={16} />
                      <span>{home.garage}</span>
                    </div>
                  </div>
                </div>



                

                {/* SDA Info */}
                <div className="mb-4">
                  <p className="font-semibold text-primary">
                    {home.isRegisteredSda ? 'Registered SDA Property' : 'Non-SDA Property'}
                  </p>
                  {home.sdaCategory && (
                    <p className="text-gray-700">SDA Category: {home.sdaCategory}</p>
                  )}
                  {home.price && (
                    <p className="text-gray-700">Rent: {home.price}%</p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
    </>
  );
};

export default HomeFinder;